import { useTranslation } from 'react-i18next';
import { supabase } from '@shared/supabase/client';
import { useSupabaseQuery } from '@shared/supabase/hooks';
import { useAuth } from '@shared/auth/AuthContext';
import { Skeleton } from '@shared/ui';
import { NamedIcon } from '@shared/ui/icons';

/**
 * Read-only view of the brands the user's ORGANIZATION can actually see in
 * analytics — resolved from organization_ad_scope via my_brand_scope(). This is
 * DISTINCT from "My brands" (ownership): it also reflects brands an admin added
 * to the org scope (e.g. agency / extra advertisers), which the ownership panel
 * does not show.
 */
export default function MyDataScope() {
  const { t } = useTranslation('customer');
  const { user } = useAuth();

  const { data: brands, isLoading } = useSupabaseQuery<{ id: string; name: string }[]>(
    ['my-brand-scope', user?.id],
    async () => {
      const { data: scope } = await supabase.rpc('my_brand_scope');
      const ids = ((scope ?? []) as { brand_id: string }[]).map((r) => r.brand_id);
      if (!ids.length) return { data: [], error: null };
      return supabase.from('brands').select('id, name').in('id', ids).order('name');
    },
    { enabled: !!user },
  );

  if (isLoading) return null;
  const rows = brands ?? [];
  if (rows.length === 0) return null; // no scope → nothing to show

  return (
    <div className="c-panel" style={{ marginTop: 20, display: 'flex', flexDirection: 'column', gap: 12 }}>
      <h3 style={{ margin: 0 }}>{t('scope.title', { defaultValue: 'Доступно в вашей аналитике' })}</h3>
      <p style={{ fontSize: 'var(--fs-body)', color: 'var(--muted)', margin: 0 }}>
        {t('scope.subtitle', {
          defaultValue: 'Бренды, по которым вашей компании доступна «Моя аналитика» (включая добавленные администратором).',
        })}
      </p>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {rows.map((b) => (
          <span
            key={b.id}
            style={{
              padding: '4px 10px',
              background: 'rgba(74,158,255,0.1)',
              border: '1px solid rgba(74,158,255,0.4)',
              borderRadius: 999,
              fontSize: 'var(--fs-body)',
            }}
          >
            <span style={{ display: 'inline-flex', verticalAlign: 'middle' }}><NamedIcon name="chart_bars" size={13} /></span> {b.name}
          </span>
        ))}
      </div>
    </div>
  );
}
